/**
 * <ReferralHandler> — 레퍼럴 링크 캡처 + 자동 claim
 *
 * 동작:
 *   1) URL `?ref=insta_xxx` 진입 시 → cookie `bs_ref` 저장 (1년) + URL 정리
 *   2) auth.loggedIn 되는 순간 → cookie 읽고 POST /api/credits/claim-referral
 *   3) 성공 시 토스트 + cookie 삭제. 실패 (이미 베타 등) 도 cookie 삭제 (재시도 X)
 *
 * 비로그인 상태에서 접속해도 cookie 가 1년 유지되어 추후 로그인 시 자동 적용.
 */
"use client";

import { useEffect, useRef } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";

const COOKIE_NAME = "bs_ref";
const COOKIE_DAYS = 365;

function setRefCookie(code: string) {
  const days = COOKIE_DAYS;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  // 도메인 prefix 도트 사용 → bullsresearch.com 과 staging.bullsresearch.com 양쪽 공유
  const host = window.location.hostname;
  const domain = host.endsWith("bullsresearch.com") ? "; domain=.bullsresearch.com" : "";
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(code)}; path=/; expires=${expires}; SameSite=Lax${domain}`;
}

function readRefCookie(): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function clearRefCookie() {
  const host = window.location.hostname;
  const domain = host.endsWith("bullsresearch.com") ? "; domain=.bullsresearch.com" : "";
  document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain}`;
}

export function ReferralHandler() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { auth, refresh } = useAuth();
  const claimedRef = useRef(false);

  // 1) URL ?ref= 감지 → cookie 저장 + URL 정리
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (!ref) return;
    setRefCookie(ref);
    // URL 에서 ref 파라미터 제거 (다른 query 는 유지)
    const params = new URLSearchParams(searchParams.toString());
    params.delete("ref");
    const qs = params.toString();
    router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
  }, [searchParams, router, pathname]);

  // 2) 로그인 감지 → cookie 있으면 claim (인스타 코드)
  useEffect(() => {
    if (!auth?.loggedIn) return;
    if (claimedRef.current) return;
    const code = readRefCookie();
    if (!code) return;
    claimedRef.current = true;

    (async () => {
      try {
        const res = await fetch("/api/ticker/credits/claim-referral", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ code }),
        });
        const data = await res.json();
        clearRefCookie();
        if (data?.ok) {
          const amount = data.tierAmount ?? data.credits ?? 300;
          toast.success(`베타 등급 활성화 + ${amount} 크레딧 지급!`, {
            description: "기업분석 보고서를 무제한으로 즐겨보세요.",
            duration: 6000,
          });
          await refresh();
          window.dispatchEvent(new CustomEvent("credits-updated"));
        }
      } catch {
        claimedRef.current = false;
      }
    })();
  }, [auth?.loggedIn, refresh]);

  // 3) 로그인 감지 → sessionStorage 에 share_ref (또는 legacy share_from) 있으면 share referral claim
  useEffect(() => {
    if (!auth?.loggedIn) return;
    if (typeof window === "undefined") return;
    const ref = sessionStorage.getItem("share_ref");
    const fromLegacy = sessionStorage.getItem("share_from");
    const reportId = sessionStorage.getItem("share_report");
    if (!ref && !fromLegacy) return;

    (async () => {
      try {
        const res = await fetch("/api/ticker/credits/claim-share-referral", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ref, from: fromLegacy, reportId }),
        });
        const data = await res.json();
        sessionStorage.removeItem("share_ref");
        sessionStorage.removeItem("share_from");
        sessionStorage.removeItem("share_report");
        if (data?.ok) {
          toast.success(`친구 공유로 가입 — +${data.newUserCredits} 크레딧 추가!`, {
            description: "공유한 친구에게도 보상이 갔어요. 첫 분석 시작해보세요.",
            duration: 6000,
          });
          window.dispatchEvent(new CustomEvent("credits-updated"));
        }
      } catch {
        /* 네트워크 에러 시 sessionStorage 유지 — 다음 로그인에 재시도 */
      }
    })();
  }, [auth?.loggedIn]);

  return null;
}
